import React, { useState } from 'react';

interface WebPImageProps {
  src: string;
  webpSrc?: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  loading?: 'lazy' | 'eager';
  onLoad?: () => void;
  onError?: () => void;
}

const WebPImage: React.FC<WebPImageProps> = ({
  src,
  webpSrc,
  alt,
  className = '',
  style = {},
  loading = 'lazy',
  onLoad,
  onError
}) => {
  const [supportsWebP, setSupportsWebP] = useState<boolean | null>(null);

  // Check WebP support
  React.useEffect(() => {
    const checkWebPSupport = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 1;
      canvas.height = 1;
      return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
    };

    setSupportsWebP(checkWebPSupport());
  }, []);

  // Generate WebP src if not provided
  const getWebPSrc = () => {
    if (webpSrc) return webpSrc;
    
    // If src is already a WebP file, use it directly
    if (src.endsWith('.webp')) {
      return src;
    }
    
    // Convert JPG/PNG to WebP by changing extension
    const baseSrc = src.replace(/\.(jpg|jpeg|png)$/i, '');
    return `${baseSrc}.webp`;
  };

  const handleLoad = () => {
    onLoad?.();
  };

  const handleError = () => {
    onError?.();
  };

  // Show nothing while checking WebP support
  if (supportsWebP === null) {
    return (
      <div className={`bg-gray-200 animate-pulse ${className}`} style={style}>
        <div className="w-full h-full bg-gray-300 rounded"></div>
      </div>
    );
  }

  return (
    <picture>
      {/* WebP source for supported browsers */}
      {supportsWebP && (
        <source
          srcSet={getWebPSrc()}
          type="image/webp"
        />
      )}
      
      {/* Fallback image */}
      <img
        src={src}
        alt={alt}
        className={className}
        style={style}
        loading={loading}
        onLoad={handleLoad}
        onError={handleError}
      />
    </picture>
  );
};

export default WebPImage;
